/**
 * Convite para instalar o validador como app.
 *
 * Mora no login, e só lá: é antes da portaria que se instala. No meio da fila
 * nenhum convite pode aparecer por cima do validador.
 *
 * Abre sozinho na primeira visita deste navegador; fechado uma vez, fica só o
 * link "Instalar aplicativo" no rodapé. Instalado, some de vez.
 *
 * O que ele diz depende do aparelho (ver src/web/instalacao.ts): no Chrome e
 * no Edge há um botão que instala com um toque; no iPhone, só os passos pelo
 * Compartilhar — e o aviso de que o app instalado não vê o que foi feito no
 * Safari, porque lá os dois guardam dados separados.
 */

import React, {useEffect, useState} from 'react';
import {Image, Modal, Pressable, Text, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {
  Download,
  EllipsisVertical,
  MonitorDown,
  Share,
  Smartphone,
  SquarePlus,
  X,
} from 'lucide-react-native';

import {ticketeira} from '../config';
import {useInstalacao, type Plataforma} from '../web/instalacao';
import {txt, useIdioma} from './idioma';
import {estilos, fontes, useTema} from './tema';

const CHAVE_DISPENSADO = 'tmobi.validador.convite-instalar';

function jaDispensado(): boolean {
  try {
    return localStorage.getItem(CHAVE_DISPENSADO) === 'sim';
  } catch {
    return false;
  }
}

function lembrarDispensa(): void {
  try {
    localStorage.setItem(CHAVE_DISPENSADO, 'sim');
  } catch {
    // Sem armazenamento, o convite volta na próxima visita — só isso.
  }
}

/** Um trecho de passo: texto comum ou em negrito (o nome do botão a tocar). */
type Trecho = string | {negrito: string};

interface Passo {
  trechos: Trecho[];
  Icone: typeof Share;
}

function passosDe(plataforma: Plataforma): Passo[] {
  if (plataforma === 'iphone') {
    return [
      {
        Icone: Share,
        trechos: [
          txt('Toque em '),
          {negrito: txt('Compartilhar')},
          txt(' — o quadrado com a seta, na barra do Safari (no iOS 26, dentro do botão ⋯)'),
        ],
      },
      {
        Icone: SquarePlus,
        trechos: [txt('Role e toque em '), {negrito: txt('Adicionar à Tela de Início')}],
      },
      {
        Icone: Smartphone,
        trechos: [
          txt('Deixe '),
          {negrito: txt('Abrir como app web')},
          txt(' ligado e toque em '),
          {negrito: txt('Adicionar')},
        ],
      },
    ];
  }
  if (plataforma === 'android') {
    return [
      {
        Icone: EllipsisVertical,
        trechos: [txt('Toque no menu '), {negrito: '⋮'}, txt(' do navegador, no canto de cima')],
      },
      {
        Icone: Download,
        trechos: [
          txt('Toque em '),
          {negrito: txt('Instalar app')},
          txt(' — ou em '),
          {negrito: txt('Adicionar à tela inicial')},
          txt(' e depois '),
          {negrito: txt('Instalar')},
        ],
      },
      {Icone: Smartphone, trechos: [txt('Abra o validador pelo ícone novo na tela inicial')]},
    ];
  }
  return [
    {
      Icone: MonitorDown,
      trechos: [
        txt('No Chrome ou no Edge, clique no ícone de '),
        {negrito: txt('instalar')},
        txt(', no fim da barra de endereço'),
      ],
    },
    {
      Icone: EllipsisVertical,
      trechos: [txt('Ou abra o menu '), {negrito: '⋮'}, txt(' e procure '), {negrito: txt('Instalar')}],
    },
  ];
}

export function ConviteInstalar() {
  const t = useTema();
  useIdioma();
  const {instalado, plataforma, instalaComUmToque, instalar} = useInstalacao();
  const [aberto, setAberto] = useState(false);

  // Primeira visita: o convite se apresenta sozinho, com um respiro para a
  // tela de login aparecer antes.
  useEffect(() => {
    if (instalado || jaDispensado()) {
      return;
    }
    const relogio = setTimeout(() => setAberto(true), 1200);
    return () => clearTimeout(relogio);
  }, [instalado]);

  if (instalado) {
    return null;
  }

  function fechar() {
    setAberto(false);
    lembrarDispensa();
  }

  async function instalarAgora() {
    const aceito = await instalar();
    if (aceito) {
      fechar();
    }
  }

  const passos = passosDe(plataforma);
  const IconeDoLink = plataforma === 'computador' ? MonitorDown : Smartphone;

  return (
    <>
      <Pressable
        onPress={() => setAberto(true)}
        accessibilityRole="button"
        style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, paddingVertical: 4}}>
        <IconeDoLink size={15} color={t.primaria} strokeWidth={2.2} />
        <Text style={{color: t.primaria, fontSize: 13, fontFamily: fontes.semi}}>
          {txt('Instalar aplicativo')}
        </Text>
      </Pressable>

      <Modal visible={aberto} transparent animationType="slide" onRequestClose={fechar}>
        <View style={{flex: 1, justifyContent: 'flex-end', alignItems: 'center', backgroundColor: t.escurecedorModal}}>
          {/* Tocar fora fecha, como em qualquer folha. */}
          <Pressable
            onPress={fechar}
            accessibilityLabel={txt('Fechar')}
            style={{position: 'absolute', left: 0, right: 0, top: 0, bottom: 0}}
          />

          <SafeAreaView
            edges={['bottom']}
            style={{
              width: '100%',
              maxWidth: 520,
              backgroundColor: t.fundo,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              borderTopWidth: 3,
              borderColor: t.primaria,
            }}>
            <View style={{padding: 22, gap: 18}}>
              <View style={{flexDirection: 'row', alignItems: 'flex-start', gap: 12}}>
                <View style={{flex: 1, gap: 4}}>
                  <Text style={[estilos.tituloCabecalho, {color: t.texto, fontSize: 21}]}>
                    {txt('Instalar o Validador')}
                  </Text>
                  <Text style={{color: t.textoFraco, fontSize: 14, fontFamily: fontes.regular, lineHeight: 20}}>
                    {plataforma === 'computador'
                      ? txt('Abre em janela própria, sem a barra do navegador, e funciona sem internet.')
                      : txt('Abre em tela cheia, sem a barra do navegador, e funciona sem internet.')}
                  </Text>
                </View>
                <Pressable
                  onPress={fechar}
                  hitSlop={10}
                  accessibilityLabel={txt('Fechar')}
                  style={{width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center'}}>
                  <X size={22} color={t.textoFraco} strokeWidth={2.2} />
                </Pressable>
              </View>

              {/* O que vai para a tela inicial: o ícone e o nome que a pessoa
                  vai procurar depois. O ícone é o `icone.png` que o servidor
                  entrega com a marca da ticketeira dele. */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 14,
                  padding: 12,
                  borderRadius: 16,
                  backgroundColor: t.superficie,
                }}>
                <Image
                  source={{uri: 'icone.png'}}
                  accessibilityLabel=""
                  style={{width: 48, height: 48, borderRadius: 12}}
                />
                <View style={{flex: 1, gap: 1}}>
                  <Text style={{color: t.texto, fontSize: 16, fontFamily: fontes.negrito}}>
                    {txt('Validador')}
                  </Text>
                  <Text numberOfLines={1} style={{color: t.textoFraco, fontSize: 13, fontFamily: fontes.regular}}>
                    {ticketeira().nome} · {t.dominio.replace(/^www\./, '')}
                  </Text>
                </View>
              </View>

              {instalaComUmToque ? null : (
                <View
                  style={{
                    backgroundColor: t.superficie,
                    borderWidth: 1,
                    borderColor: t.borda,
                    borderRadius: 16,
                    padding: 16,
                    gap: 16,
                  }}>
                  {passos.map(({trechos, Icone}, indice) => (
                    <View key={indice} style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
                      <View
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 14,
                          backgroundColor: t.superficieAlta,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Text style={{color: t.primaria, fontSize: 14, fontFamily: fontes.extra}}>
                          {indice + 1}
                        </Text>
                      </View>
                      <Text style={{flex: 1, color: t.textoMedio, fontSize: 15, fontFamily: fontes.regular, lineHeight: 21}}>
                        {trechos.map((trecho, i) =>
                          typeof trecho === 'string' ? (
                            trecho
                          ) : (
                            <Text key={i} style={{color: t.texto, fontFamily: fontes.negrito}}>
                              {trecho.negrito}
                            </Text>
                          ),
                        )}
                      </Text>
                      <Icone size={20} color={t.textoFraco} strokeWidth={2} />
                    </View>
                  ))}
                </View>
              )}

              {/* No iPhone o app instalado guarda dados à parte do Safari: o
                  que foi validado numa aba do Safari não aparece nele. Quem
                  instala no meio do evento deixa validação presa para trás. */}
              {plataforma === 'iphone' ? (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 10,
                    padding: 14,
                    borderRadius: 14,
                    backgroundColor: t.avisoFundo,
                    borderWidth: 1,
                    borderColor: t.avisoBorda,
                  }}>
                  <Text style={{color: t.avisoTinta, fontSize: 13, fontFamily: fontes.negrito}}>!</Text>
                  <Text style={{flex: 1, color: t.avisoTinta, fontSize: 13, fontFamily: fontes.semi, lineHeight: 19}}>
                    {txt(
                      'Instale antes de começar a portaria. No iPhone, o app instalado não vê o que foi feito pelo Safari.',
                    )}
                  </Text>
                </View>
              ) : null}

              {instalaComUmToque ? (
                <Pressable onPress={instalarAgora} style={[estilos.botao, {backgroundColor: t.primaria}]}>
                  <Download size={20} color={t.tintaPrimaria} strokeWidth={2.4} />
                  <Text style={[estilos.textoBotao, {color: t.tintaPrimaria}]}>
                    {txt('Instalar aplicativo')}
                  </Text>
                </Pressable>
              ) : (
                <Pressable
                  onPress={fechar}
                  style={[estilos.botao, {borderWidth: 1, borderColor: t.borda}]}>
                  <Text style={[estilos.textoBotao, {color: t.textoMedio, fontSize: 16}]}>
                    {txt('Entendi')}
                  </Text>
                </Pressable>
              )}
            </View>
          </SafeAreaView>
        </View>
      </Modal>
    </>
  );
}
